import React, { useEffect, useMemo, useState } from "react"
import {
	Autocomplete, Box, Chip, Stack, TextField, Typography,
} from "@mui/material"
import { ArrowRightAlt as ArrowIcon } from 'Pages/DsRepricing/@components/icons'
import { IconSeeker } from "@ui-seeker/core"
import { dsColorNew } from "@palette"

import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import { setupApi } from "Pages/DsRepricing/Setup/@services"
import { useCategories } from "Pages/DsRepricing/Setup/@services/useCategories"

/**
 * Tethers · per-strategy selection.
 *
 * The tethering rules themselves live in Setup → Tethering; each
 * strategy just picks WHICH of those existing rules should apply to
 * its own repricings. The list here is a subset of setup rules; when
 * a rule is deleted from Setup the reference here is dropped
 * automatically on next open (`syncFromLibrary`).
 */

const LORLABEL = {
	'1':'1 day','2':'2 days','3':'3 days','4':'4 days','5':'5 days',
	'7':'7 days','10':'10 days','14':'14 days','21':'21 days',
	'28':'28 days','30':'30+ days',
}
const DIM_PALETTE = {
	lor:      { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder, mono: false },
	category: { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder, mono: false },
	acriss:   { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder, mono: true  },
	office:   { bg: dsColorNew.successBg, fg: dsColorNew.successText, border: dsColorNew.successBar, mono: true  },
}
const DIM_LABEL = { lor: 'LOR', category: 'Category', acriss: 'ACRISS', office: 'Office' }

/**
 * The chip label for a tether value.
 *
 * Only `category` needs a catalogue, and it used to read the
 * hand-written `ACRISS_TREE`. `useShortLabel` supplies the tenant's
 * own classes instead; the plain function is kept for the dimensions
 * that need nothing (Curro, 2026-09-28: "nada mock ya").
 */
function shortLabel(dim, code, categories = []) {
	if (dim === 'lor')      return LORLABEL[code] || `${code}d`
	if (dim === 'category') return categories.find(m => m.code === code)?.name || code
	return code
}

function useShortLabel() {
	const { tree } = useCategories()
	return React.useCallback((dim, code) => shortLabel(dim, code, tree), [tree])
}

function formatDelta(d) {
	if (!d || d.v == null) return ''
	const sign = d.v > 0 ? '+' : ''
	return d.k === 'pct' ? `${sign}${d.v}%` : `${sign}${d.v}€`
}

/**
 * The tether library of the tenant, from Setup → Tethering
 * (`/api/repricing/setup/tethers`). The strategy only ever stores ids;
 * the definitions belong to Setup, which is why there is no inline
 * creation here (PRD 02 §3).
 *
 * Read once when the step opens. A tether created in another tab appears
 * on the next open, which is the same guarantee the rest of the wizard
 * gives for offices and pricelists.
 */
function useTetherLibrary() {
	const [library, setLibrary] = useState([])

	useEffect(() => {
		let alive = true
		setupApi.tethers.list()
			.then(rows => alive && setLibrary(rows.map(t => ({
				id: t.id,
				name: t.name,
				dimension: t.dimension,
				parent: t.parent_value,
				children: (t.children || []).map(c => c.child_value ?? c),
				delta: { k: t.delta_kind, v: Number(t.delta_value) },
			}))))
			.catch(() => alive && setLibrary([]))
		return () => { alive = false }
	}, [])

	return library
}

export default function DsRepricingTethers() {
	const data = dsRepricingDrawerData.value
	const library = useTetherLibrary()
	const selectedIds = Array.isArray(data.tethers) ? data.tethers : []

	// Drop references to tethers that no longer exist in the library
	// (someone deleted them from Setup). Runs on every render change of
	// `library` — cheap since library is small.
	useEffect(() => {
		if (!selectedIds.length || !library.length) return
		const liveIds = new Set(library.map(t => t.id))
		const cleaned = selectedIds.filter(id => liveIds.has(id))
		if (cleaned.length !== selectedIds.length) {
			dsRepricingDrawerData.value = { ...dsRepricingDrawerData.peek(), tethers: cleaned }
		}
	}, [library])   // eslint-disable-line react-hooks/exhaustive-deps

	function onChange(next) {
		dsRepricingDrawerData.value = {
			...dsRepricingDrawerData.peek(),
			tethers: next.map(t => t.id),
		}
	}

	const selected = library.filter(t => selectedIds.includes(t.id))

	if (library.length === 0) {
		return (
			<Box sx={{ p: 1.25, bgcolor: dsColorNew.tableRowAlt,
			           border: `1px dashed ${dsColorNew.grayBorder}`,
			           borderRadius: '10px', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
				<IconSeeker icon="circle-info"
				            style={{ color: dsColorNew.textMuted, marginTop: 3, fontSize: 16 }}/>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
				                  lineHeight: 1.5 }}>
					No tethers defined yet. Create them in{' '}
					<strong>Setup → Tethering</strong> to make them selectable here.
				</Typography>
			</Box>
		)
	}

	return (
		<Stack gap={1}>
			<Autocomplete multiple size="small"
			              options={library}
			              value={selected}
			              disableCloseOnSelect
			              groupBy={(t) => DIM_LABEL[t.dimension] || t.dimension}
			              getOptionLabel={(t) => t.name}
			              isOptionEqualToValue={(a, b) => a.id === b.id}
			              onChange={(_, next) => onChange(next)}
			              renderTags={(list, getTagProps) =>
			                list.map((t, idx) => (
			                  <TetherTag {...getTagProps({ index: idx })}
			                             key={t.id} tether={t}/>
			                ))}
			              renderOption={(props, t) => (
			                <li {...props} key={t.id}
			                    style={{ ...props.style, alignItems: 'flex-start',
			                             paddingTop: 6, paddingBottom: 6 }}>
			                  <TetherOptionRow tether={t}/>
			                </li>
			              )}
			              renderInput={(params) => (
			                <TextField {...params}
			                           placeholder={selected.length ? '' : 'Pick one or more tethers…'}/>
			              )}/>

			{selected.length > 0 && (
				<Stack gap={0.5}>
					{selected.map(t => (
						<TetherPreviewRow key={t.id} tether={t}/>
					))}
				</Stack>
			)}
		</Stack>
	)
}

function TetherTag({ tether, ...rest }) {
	const p = DIM_PALETTE[tether.dimension] || {}
	return (
		<Chip {...rest} size="small"
		      label={tether.name}
		      sx={{ height: 22, fontSize: 11, fontWeight: 700,
		            bgcolor: p.bg, color: p.fg,
		            border: '1px solid', borderColor: p.border }}/>
	)
}

function TetherOptionRow({ tether }) {
	const shortLabel = useShortLabel()
	const p = DIM_PALETTE[tether.dimension] || {}
	const children = tether.children || (tether.child ? [tether.child] : [])
	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
			<Stack direction="row" gap={0.75} alignItems="center">
				<Chip size="small" label={DIM_LABEL[tether.dimension] || tether.dimension}
				      sx={{ height: 18, fontSize: 11, fontWeight: 700,
				            bgcolor: p.bg, color: p.fg,
				            border: '1px solid', borderColor: p.border,
				            letterSpacing: '0.04em' }}/>
				<Typography sx={{ fontSize: 13, fontWeight: 700 }}>
					{tether.name}
				</Typography>
			</Stack>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
			                  fontFamily: p.mono ? 'inherit' : 'inherit' }}>
				{shortLabel(tether.dimension, tether.parent)}{' '}→{' '}
				{children.map(c => shortLabel(tether.dimension, c)).join(', ') || '…'}
				{'  ·  '}<Box component="span" sx={{ fontWeight: 700, fontFamily: 'inherit',
				                                     color: (tether.delta?.v ?? 0) >= 0 ? dsColorNew.successText : dsColorNew.dangerRed }}>
					{formatDelta(tether.delta)}
				</Box>
			</Typography>
		</Box>
	)
}

function TetherPreviewRow({ tether }) {
	const shortLabel = useShortLabel()
	const p = DIM_PALETTE[tether.dimension] || {}
	const children = tether.children || (tether.child ? [tether.child] : [])
	const chipSx = {
		height: 22, fontSize: 12, fontWeight: 700,
		bgcolor: dsColorNew.white,
		border: '1px solid', borderColor: p.border,
		color: p.fg,
		fontFamily: p.mono ? 'inherit' : 'inherit',
		'& .MuiChip-label': { px: 0.75 },
	}
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
		           px: 1, py: 0.5,
		           bgcolor: dsColorNew.tableRowAlt, border: `1px solid ${dsColorNew.grayBorder}`,
		           borderRadius: '8px' }}>
			<Chip size="small" label={shortLabel(tether.dimension, tether.parent)} sx={chipSx}/>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 6 }}/>
				<Box sx={{ fontSize: 11, fontWeight: 700,
				           bgcolor: dsColorNew.white,
				           border: '1px solid', borderColor: dsColorNew.grayBorder,
				           borderRadius: 0.5, px: 0.5, py: 0.1,
				           color: (tether.delta?.v ?? 0) > 0 ? dsColorNew.successText
				                : (tether.delta?.v ?? 0) < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
				           fontFamily: 'inherit' }}>
					{formatDelta(tether.delta)}
				</Box>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 6 }}/>
				<ArrowIcon sx={{ fontSize: 18, color: dsColorNew.textSubtle }}/>
			</Box>
			<Stack direction="row" gap={0.35} flexWrap="wrap">
				{children.length === 0
				  ? <Chip size="small" label="—"
				          sx={{ ...chipSx, borderStyle: 'dashed', color: dsColorNew.textSubtle }}/>
				  : children.map(c => (
				      <Chip key={c} size="small"
				            label={shortLabel(tether.dimension, c)}
				            sx={chipSx}/>
				    ))}
			</Stack>
		</Box>
	)
}
